import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

const DOW = ['mon','tue','wed','thu','fri','sat']
const DOW_LABELS = { mon: 'MON', tue: 'TUE', wed: 'WED', thu: 'THU', fri: 'FRI', sat: 'SAT' }

export default function ProfilePage() {
  const { profile, isOwner } = useAuth()
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', new_password: '', confirm_password: '' })
  const [availability, setAvailability] = useState({ season_start: '', season_end: '', preferred_days_off: [] })
  const [changesThisYear, setChangesThisYear] = useState(0)
  const [saving, setSaving] = useState(false)
  const [savingAvail, setSavingAvail] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const MAX_CHANGES = 2

  const fetchChangeCount = useCallback(async () => {
    const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString()
    const { count } = await supabase.from('contact_info_changes').select('*', { count: 'exact', head: true }).eq('user_id', profile.id).gte('created_at', startOfYear)
    setChangesThisYear(count || 0)
  }, [profile])

  useEffect(() => {
    if (profile) {
      setForm(f => ({ ...f, full_name: profile.full_name || '', email: profile.email || '', phone: profile.phone || '' }))
      setAvailability({
        season_start: profile.season_start || '',
        season_end: profile.season_end || '',
        preferred_days_off: profile.preferred_days_off || [],
      })
      fetchChangeCount()
    }
  }, [profile, fetchChangeCount])

  const toggleDayOff = (d) => {
    setAvailability((a) => {
      const has = a.preferred_days_off.includes(d)
      return { ...a, preferred_days_off: has ? a.preferred_days_off.filter((x) => x !== d) : [...a.preferred_days_off, d] }
    })
  }

  const handleSaveAvailability = async () => {
    setSavingAvail(true)
    const patch = {
      season_start: availability.season_start || null,
      season_end: availability.season_end || null,
      preferred_days_off: availability.preferred_days_off,
    }
    // Let the worker's status roll to 'available' immediately if they're now in-season
    const today = new Date().toISOString().slice(0, 10)
    if (patch.season_start && patch.season_end) {
      if (today >= patch.season_start && today <= patch.season_end) {
        if (profile.availability_status !== 'inactive') patch.availability_status = 'available'
      } else {
        if (profile.availability_status !== 'inactive') patch.availability_status = 'off_season'
      }
    }
    const { error } = await supabase.from('profiles').update(patch).eq('id', profile.id)
    if (error) { setError(error.message); setTimeout(() => setError(''), 4000) }
    else { setMessage('Availability saved.'); setTimeout(() => setMessage(''), 4000) }
    setSavingAvail(false)
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const showMsg = (msg, isError = false) => {
    if (isError) setError(msg)
    else setMessage(msg)
    setTimeout(() => { setMessage(''); setError('') }, 4000)
  }

  const canChangeContact = isOwner || changesThisYear < MAX_CHANGES
  const remainingChanges = MAX_CHANGES - changesThisYear

  const handleSaveProfile = async () => {
    setSaving(true)
    const contactChanged = form.email !== profile.email || form.phone !== profile.phone
    if (contactChanged && !isOwner) {
      if (changesThisYear >= MAX_CHANGES) { showMsg('You have used all 2 contact info changes for this year. Resets January 1st.', true); setSaving(false); return }
      await supabase.from('contact_info_changes').insert({ user_id: profile.id, old_email: profile.email, new_email: form.email, old_phone: profile.phone, new_phone: form.phone })
      setChangesThisYear(c => c + 1)
    }
    const { error } = await supabase.from('profiles').update({ full_name: form.full_name, email: form.email, phone: form.phone }).eq('id', profile.id)
    if (error) showMsg(error.message, true)
    else showMsg('Profile updated successfully.')
    setSaving(false)
  }

  const handleChangePassword = async () => {
    if (form.new_password !== form.confirm_password) { showMsg('Passwords do not match.', true); return }
    if (form.new_password.length < 8) { showMsg('Password must be at least 8 characters.', true); return }
    setSaving(true)
    const { error } = await supabase.auth.updateUser({ password: form.new_password })
    if (error) showMsg(error.message, true)
    else { showMsg('Password updated.'); setForm(f => ({ ...f, new_password: '', confirm_password: '' })) }
    setSaving(false)
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.avatar}>{profile?.full_name?.charAt(0)?.toUpperCase() || '?'}</div>
        <div>
          <div style={styles.title}>{profile?.full_name || 'My Profile'}</div>
          <div style={styles.role}>{profile?.role?.replace('_', ' ').toUpperCase()}</div>
          <div style={styles.businesses}>
            {profile?.business_ids?.map(b => (
              <span key={b} style={styles.bizChip}>{b === 'pools' ? 'CP Quality Pools' : 'Riverbend Clearing'}</span>
            ))}
          </div>
        </div>
      </div>
      {message && <div style={styles.successMsg}>{message}</div>}
      {error && <div style={styles.errorMsg}>{error}</div>}
      {!isOwner && (
        <div style={styles.changeCounter}>
          <div style={styles.counterLabel}>CONTACT INFO CHANGES THIS YEAR</div>
          <div style={styles.counterDots}>
            {[0, 1].map(i => <div key={i} style={{ ...styles.counterDot, ...(i < changesThisYear ? styles.counterDotUsed : {}) }} />)}
          </div>
          <div style={styles.counterText}>
            {changesThisYear >= MAX_CHANGES ? 'No changes remaining. Resets January 1st.' : `${remainingChanges} of ${MAX_CHANGES} changes remaining this year`}
          </div>
        </div>
      )}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>PERSONAL INFORMATION</div>
        <div style={styles.formGrid}>
          <div style={styles.field}><label style={styles.label}>FULL NAME</label><input value={form.full_name} onChange={e => set('full_name', e.target.value)} style={styles.input} placeholder="First Last" /></div>
          <div style={styles.field}><label style={styles.label}>EMAIL{!isOwner && <span style={styles.limitedLabel}> � LIMITED</span>}</label><input value={form.email} onChange={e => set('email', e.target.value)} style={{ ...styles.input, ...(!canChangeContact ? styles.inputDisabled : {}) }} placeholder="your@email.com" type="email" disabled={!canChangeContact} /></div>
          <div style={styles.field}><label style={styles.label}>PHONE{!isOwner && <span style={styles.limitedLabel}> � LIMITED</span>}</label><input value={form.phone} onChange={e => set('phone', e.target.value)} style={{ ...styles.input, ...(!canChangeContact ? styles.inputDisabled : {}) }} placeholder="8109236229" disabled={!canChangeContact} /></div>
        </div>
        <div style={styles.actionRow}><button onClick={handleSaveProfile} disabled={saving} style={styles.saveBtn}>{saving ? 'SAVING...' : 'SAVE PROFILE'}</button></div>
      </div>
      {profile?.role !== 'store_partner' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>MY AVAILABILITY</div>
          <div style={styles.availStatusRow}>
            <div>
              <div style={styles.availStatusLabel}>CURRENT STATUS</div>
              <div style={{ ...styles.availStatusPill, ...(profile?.availability_status === 'available' ? styles.availGreen : profile?.availability_status === 'inactive' ? styles.availRed : styles.availGrey) }}>
                {(profile?.availability_status || 'off_season').toUpperCase().replace('_', ' ')}
              </div>
            </div>
            {profile?.hire_date && (
              <div>
                <div style={styles.availStatusLabel}>HIRED</div>
                <div style={styles.availStatusValue}>{new Date(profile.hire_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
              </div>
            )}
            {profile?.seniority_rank && (
              <div>
                <div style={styles.availStatusLabel}>SENIORITY</div>
                <div style={styles.availStatusValue}>#{profile.seniority_rank}</div>
              </div>
            )}
          </div>
          <div style={styles.formGrid}>
            <div style={styles.field}>
              <label style={styles.label}>SEASON START</label>
              <input type="date" value={availability.season_start} onChange={(e) => setAvailability(a => ({ ...a, season_start: e.target.value }))} style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>SEASON END</label>
              <input type="date" value={availability.season_end} onChange={(e) => setAvailability(a => ({ ...a, season_end: e.target.value }))} style={styles.input} />
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>PREFERRED DAYS OFF</label>
            <div style={styles.dowRow}>
              {DOW.map((d) => {
                const active = availability.preferred_days_off.includes(d)
                return (
                  <button key={d} type="button" onClick={() => toggleDayOff(d)} style={{ ...styles.dowBtn, ...(active ? styles.dowBtnActive : {}) }}>
                    {DOW_LABELS[d]}
                  </button>
                )
              })}
            </div>
          </div>
          <div style={styles.actionRow}>
            <button onClick={handleSaveAvailability} disabled={savingAvail} style={styles.saveBtn}>
              {savingAvail ? 'SAVING...' : 'SAVE AVAILABILITY'}
            </button>
          </div>
        </div>
      )}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>CHANGE PASSWORD</div>
        <div style={styles.formGrid}>
          <div style={styles.field}><label style={styles.label}>NEW PASSWORD</label><input value={form.new_password} onChange={e => set('new_password', e.target.value)} style={styles.input} type="password" placeholder="Min 8 characters" /></div>
          <div style={styles.field}><label style={styles.label}>CONFIRM PASSWORD</label><input value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} style={styles.input} type="password" placeholder="Repeat password" /></div>
        </div>
        <div style={styles.actionRow}><button onClick={handleChangePassword} disabled={saving || !form.new_password} style={styles.saveBtn}>{saving ? 'SAVING...' : 'UPDATE PASSWORD'}</button></div>
      </div>
    </div>
  )
}

const styles = {
  container: { maxWidth: '800px', fontFamily: "'Barlow', sans-serif" },
  header: { display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '36px' },
  avatar: { width: '72px', height: '72px', backgroundColor: 'rgba(180,140,60,0.15)', border: '2px solid rgba(180,140,60,0.3)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '28px', color: '#b48c3c', flexShrink: 0 },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '26px', fontWeight: 700, letterSpacing: '2px', color: '#ffffff' },
  role: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', color: '#b48c3c', marginTop: '4px' },
  businesses: { display: 'flex', gap: '8px', marginTop: '8px' },
  bizChip: { backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '3px 10px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1px', color: 'rgba(255,255,255,0.4)' },
  successMsg: { backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', padding: '12px 16px', color: '#4a8c6a', fontSize: '14px', marginBottom: '20px' },
  errorMsg: { backgroundColor: 'rgba(220,60,60,0.1)', border: '1px solid rgba(220,60,60,0.3)', borderRadius: '3px', padding: '12px 16px', color: '#ff6b6b', fontSize: '14px', marginBottom: '20px' },
  changeCounter: { backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.15)', borderRadius: '4px', padding: '16px 20px', marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '16px' },
  counterLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', flexShrink: 0 },
  counterDots: { display: 'flex', gap: '8px' },
  counterDot: { width: '12px', height: '12px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' },
  counterDotUsed: { backgroundColor: '#b48c3c', borderColor: '#b48c3c' },
  counterText: { fontSize: '13px', color: 'rgba(255,255,255,0.4)' },
  section: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '28px', marginBottom: '20px' },
  sectionTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '3px', color: 'rgba(255,255,255,0.4)', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' },
  field: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)' },
  limitedLabel: { color: '#b48c3c' },
  input: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '12px 14px', color: '#ffffff', fontSize: '15px', fontFamily: "'Barlow', sans-serif", outline: 'none' },
  inputDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  actionRow: { display: 'flex', justifyContent: 'flex-end' },
  saveBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '12px 24px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  availStatusRow: { display: 'flex', gap: '24px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.05)', flexWrap: 'wrap' },
  availStatusLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '6px' },
  availStatusValue: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', color: '#ffffff', fontWeight: 500 },
  availStatusPill: { display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1.5px', fontWeight: 700 },
  availGreen: { backgroundColor: 'rgba(74,140,106,0.15)', color: '#6fbf8e', border: '1px solid rgba(74,140,106,0.4)' },
  availGrey: { backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.12)' },
  availRed: { backgroundColor: 'rgba(224,92,92,0.12)', color: '#e05c5c', border: '1px solid rgba(224,92,92,0.4)' },
  dowRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  dowBtn: { flex: '1 1 60px', padding: '10px 8px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  dowBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
}

